import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { tenant } from "@/api";
import { useAuth } from "@/context/AuthContext";


export const DEFAULT_TENANT_FEATURES = Object.freeze({
  commercialEnabled: true,
  documentsEnabled: true,
  gmaoEnabled: false,
  acquisitionEnabled: true,
});


const TenantFeaturesContext = createContext(null);


function normalizeTenantFeatures(payload) {
  const features = payload?.features || {};

  return {
    commercialEnabled:
      features.commercialEnabled !== false,
    documentsEnabled:
      features.documentsEnabled !== false,
    gmaoEnabled:
      features.gmaoEnabled === true,
    acquisitionEnabled:
      features.acquisitionEnabled !== false,
  };
}


export function TenantFeaturesProvider({ children }) {
  const { user } = useAuth();

  const [tenantId, setTenantId] = useState(null);
  const [features, setFeatures] = useState(
    DEFAULT_TENANT_FEATURES
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");


  const refresh = useCallback(async () => {
    if (!user) {
      setTenantId(null);
      setFeatures(DEFAULT_TENANT_FEATURES);
      setError("");
      setLoading(false);

      return DEFAULT_TENANT_FEATURES;
    }

    setLoading(true);
    setError("");

    try {
      const data = await tenant.getFeatures();
      const normalizedFeatures =
        normalizeTenantFeatures(data);

      setTenantId(
        data?.tenantId || user.tenantId || null
      );
      setFeatures(normalizedFeatures);

      return normalizedFeatures;
    } catch {
      /*
       * Compatibilité sécurisée :
       * - les modules historiques restent disponibles ;
       * - la GMAO reste désactivée ;
       * - aucune erreur de chargement ne doit casser l'application.
       */
      setTenantId(user.tenantId || null);
      setFeatures(DEFAULT_TENANT_FEATURES);
      setError(
        "Impossible de charger les modules du tenant."
      );

      return DEFAULT_TENANT_FEATURES;
    } finally {
      setLoading(false);
    }
  }, [user]);


  useEffect(() => {
    refresh();
  }, [refresh]);


  const hasFeature = useCallback(
    (featureName) => features[featureName] === true,
    [features]
  );


  const value = useMemo(
    () => ({
      tenantId,
      features,
      loading,
      error,
      refresh,
      hasFeature,
    }),
    [
      tenantId,
      features,
      loading,
      error,
      refresh,
      hasFeature,
    ]
  );


  return (
    <TenantFeaturesContext.Provider value={value}>
      {children}
    </TenantFeaturesContext.Provider>
  );
}


export function useTenantFeatures() {
  const context = useContext(TenantFeaturesContext);

  if (!context) {
    throw new Error(
      "useTenantFeatures doit être utilisé dans TenantFeaturesProvider"
    );
  }

  return context;
}

export function useOptionalTenantFeatures() {
  return useContext(TenantFeaturesContext);
}
